import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { SentryService } from '@box-core/services';
import { Order, APIError, OrderHistoryGroup } from '@box-types';
import { ReviewDialogResponse } from '@box-rating-widget/components/review-dialog/review-dialog.types';
import { Subscription } from 'rxjs';
import { finalize, skip } from 'rxjs/operators';
import { AccountHistoryOrdersService } from './account-history-orders.service';
import { getOrderHistoryGroupsByStatus } from '@box/utils';

@Component({
  selector: 'account-history-orders',
  templateUrl: './account-history-orders.component.html',
  styleUrls: ['./account-history-orders.component.scss'],
  providers: [AccountHistoryOrdersService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountHistoryOrdersComponent implements OnInit, OnDestroy {
  public loading: boolean;
  public readonly emptyStateOptions = {
    iconURL: '/assets/images/empty-state/cart-empty-state-food.svg',
    title: 'you_do_not_have_previous_orders',
    description: 'choose_and_start_now'
  };

  public orders: Order[];
  public inProgressGroups: OrderHistoryGroup[];
  public scheduledGroups: OrderHistoryGroup[];
  public completedGroups: OrderHistoryGroup[];

  private ordersSubscription: Subscription;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private accountHistoryOrdersService: AccountHistoryOrdersService,
    private sentryService: SentryService
  ) {}

  @HostBinding('class') public hostClass = 'account-history-orders';

  ngOnInit(): void {
    this.setOrdersSubscription();
    this.getOrders();
  }

  ngOnDestroy(): void {
    this.ordersSubscription?.unsubscribe();
  }

  public onOrderClick(order: Order): void {
    this.accountHistoryOrdersService.handleOrderSelection(order);
  }

  public onOrderRate(order: Order, rating: number): void {
    this.accountHistoryOrdersService
      .handleOrderRateShop(order, rating)
      .afterClosed()
      .subscribe((data: ReviewDialogResponse) => {
        if (!data?.review) return;
        this.accountHistoryOrdersService.updateOrderHistoryAfterRate(order, data.review.rating);
        this.getOrders();
      });
  }

  private getOrders(): void {
    this.loading = true;
    this.changeDetectorRef.detectChanges();
    this.accountHistoryOrdersService
      .getOrders$()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe({
        next: (orders) => this.accountHistoryOrdersService.setOrders(orders),
        error: (error: Error | APIError) => {
          this.sentryService.captureException(error, {
            domain: 'Orders',
            domainDetails: 'Account History Orders',
            severity: 'warning'
          });
        }
      });
  }

  private setOrdersSubscription(): void {
    this.ordersSubscription = this.accountHistoryOrdersService.orders$.pipe(skip(1)).subscribe((orders) => {
      this.orders = orders;
      this.inProgressGroups = getOrderHistoryGroupsByStatus(this.orders, 'INPROGRESS');
      this.scheduledGroups = getOrderHistoryGroupsByStatus(this.orders, 'SCHEDULED');
      this.completedGroups = getOrderHistoryGroupsByStatus(this.orders, 'COMPLETED');
      this.changeDetectorRef.detectChanges();
    });
  }
}
